import styled from "@emotion/styled";
import { Flex } from "styles/utility-components";

import DocumentViewer from "components/bid_module/DocumentViewer";
import Button from "components/ui/button/Button";
import Drawer from "components/ui/drawer/Drawer";
import { FileViewerContextProvider } from "lib/core_components/FileViewer";
// Types for locations and props
import { BidReportEntry_Answer_Sources } from "lib/generated/app-service-gql/graphql";
import { useDocument } from "lib/hooks/api/documents/useDocument";
import { useLogEntityViewOnOpen } from "lib/hooks/api/userActivity/useLogEntityView";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { LoggedEntityType } from "lib/types/graphQLEnums";

export type BuyerDocumentDrawerProps = {
  documentId: string;
  source?: BidReportEntry_Answer_Sources;
  isOpen: boolean;
  onClose: () => void;
};

export function BuyerDocumentDrawer({
  documentId,
  source,
  isOpen,
  onClose,
}: BuyerDocumentDrawerProps) {
  const { data, isLoading } = useDocument(documentId, { enabled: !!documentId });
  useLogEntityViewOnOpen(LoggedEntityType.BuyerDocument, isOpen ? documentId : undefined);
  const pdfUrl = data?.pdfDocumentUrl;
  const fileExtension = "pdf"; // Always pdf for buyer documents

  return (
    <Drawer
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      width={1200}
    >
      <FileViewerContextProvider key={`${pdfUrl}-${!!pdfUrl}`}>
        <PreviewWrapper>
          <Controls>
            <Button
              buttonType="secondary"
              title="Close"
              icon="chevronRightDouble"
              onClick={onClose}
            />
          </Controls>
          {isLoading ? (
            <Flex alignItems="center" justifyContent="center" width="100%">
              <UIcon icon="spinner" size={32} />
            </Flex>
          ) : pdfUrl ? (
            <>
              <DocumentViewer
                documentUrl={pdfUrl}
                fileExtension={fileExtension}
                source={{ ...source, documentId, locations: source?.locations ?? [] }}
                open={true}
              />
            </>
          ) : (
            <Flex alignItems="center" justifyContent="center" width="100%">
              <span>Document not found.</span>
            </Flex>
          )}
        </PreviewWrapper>
      </FileViewerContextProvider>
    </Drawer>
  );
}

const PreviewWrapper = styled(Flex)({
  paddingRight: 16,
  height: "100vh",
  overflow: "hidden",
});

const Controls = styled(Flex)(({ theme }) => ({
  flexDirection: "column",
  alignItems: "center",
  borderRight: `1px solid ${theme.colors.sysBorderDecorative}`,
  height: "100vh",
  padding: 16,
}));
